import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, MessageCircle, User, Compass, MoreHorizontal, Timer, Trophy, Book, Award, Settings, Users, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';

interface MoreMenuItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

const moreMenuItems: MoreMenuItem[] = [
  { href: '/focus', label: 'Focus Timer', icon: Timer, description: 'Start a reading session' },
  { href: '/search', label: 'Search', icon: Search, description: 'Find books and readers' },
  { href: '/achievements', label: 'Achievements', icon: Trophy, description: 'View your progress' },
  { href: '/series-tracker', label: 'Series Tracker', icon: Book, description: 'Track book series' },
  { href: '/buddy-reads', label: 'Dino Duos', icon: Users, description: 'Read with friends' },
  { href: '/awards', label: 'Book Awards', icon: Award, description: 'Discover award winners' },
  { href: '/settings', label: 'Settings', icon: Settings, description: 'Preferences & account' },
];

export function MobileNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getTotalUnreadCount } = useChats();
  const [username, setUsername] = useState<string>('');
  const [moreOpen, setMoreOpen] = useState(false);
  
  const unreadCount = getTotalUnreadCount();

  useEffect(() => {
    const fetchUsername = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      
      if (data?.username) {
        setUsername(data.username);
      }
    };
    
    fetchUsername();
  }, [user]);

  if (!user) return null;

  const handleMoreItemClick = (href: string) => {
    setMoreOpen(false);
    navigate(href);
  };

  const navItems = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/discover', label: 'Discover', icon: Compass },
    { 
      href: '/inbox', 
      label: 'Messages', 
      icon: MessageCircle,
      badge: unreadCount > 0 ? (unreadCount >= 9 ? '9+' : unreadCount.toString()) : undefined
    },
    { href: username ? `/u/${username}` : '/profile', label: 'Profile', icon: User },
  ];

  // Check if current route is in the "More" menu
  const isMoreActive = moreMenuItems.some(item => location.pathname === item.href);

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t shadow-soft">
      <div className="flex items-center justify-around py-2 px-2">
        {navItems.map(({ href, label, icon: Icon, badge }) => {
          const isActive = location.pathname === href;
          
          return (
            <Link
              key={href}
              to={href}
              className={cn(
                "relative flex flex-col items-center justify-center py-1.5 px-2 rounded-lg transition-colors",
                "min-h-[48px] min-w-[48px]",
                isActive 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Icon className="w-5 h-5 mb-0.5" />
              <span className="text-[10px] font-medium">{label}</span>
              
              {badge && (
                <Badge 
                  variant="destructive" 
                  className="absolute top-0 right-0 h-4 min-w-4 text-[10px] px-1 flex items-center justify-center"
                >
                  {badge}
                </Badge>
              )}
            </Link>
          );
        })}

        {/* More Button with Drawer */}
        <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
          <DrawerTrigger asChild>
            <button
              className={cn(
                "relative flex flex-col items-center justify-center py-1.5 px-2 rounded-lg transition-colors",
                "min-h-[48px] min-w-[48px]",
                isMoreActive
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <MoreHorizontal className="w-5 h-5 mb-0.5" />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="border-b pb-4">
              <div className="flex items-center justify-between">
                <DrawerTitle className="text-lg font-semibold">More Options</DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </DrawerClose>
              </div>
            </DrawerHeader>
            <div className="p-4 space-y-1 overflow-y-auto">
              {moreMenuItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <button
                    key={item.href}
                    onClick={() => handleMoreItemClick(item.href)}
                    className={cn(
                      "w-full flex items-center gap-4 p-3 rounded-lg transition-colors text-left",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50 text-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-full",
                      isActive ? "bg-primary/20" : "bg-muted"
                    )}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </nav>
  );
}
